const DEFAULT_BPM = 120;
const SECONDS_PER_MINUTE = 60;

const normalizeTempos = (
  tempos: { bpm: number; ticks: number }[]
): { bpm: number; ticks: number }[] => {
  const sorted = tempos
    .filter((t) => Number.isFinite(t.bpm) && t.bpm > 0)
    .map((t) => ({ bpm: t.bpm, ticks: Math.max(0, t.ticks) }))
    .sort((a, b) => a.ticks - b.ticks);
  if (sorted.length === 0) return [{ bpm: DEFAULT_BPM, ticks: 0 }];
  if (sorted[0]!.ticks > 0) return [{ bpm: DEFAULT_BPM, ticks: 0 }, ...sorted];
  return sorted;
};

const tickDeltaToSeconds = (
  ticks: number,
  bpm: number,
  ppq: number
): number => (ticks / ppq) * (SECONDS_PER_MINUTE / bpm);

const secondsToTickDelta = (
  seconds: number,
  bpm: number,
  ppq: number
): number => (seconds * ppq * bpm) / SECONDS_PER_MINUTE;

export const tickToSeconds = (
  tick: number,
  tempos: { bpm: number; ticks: number }[],
  ppq: number
): number => {
  if (!Number.isFinite(tick) || tick <= 0) return 0;
  const safePpq = ppq > 0 ? ppq : 480;
  const segments = normalizeTempos(tempos);
  let seconds = 0;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!;
    const segStart = seg.ticks;
    const segEnd =
      i + 1 < segments.length ? segments[i + 1]!.ticks : Number.POSITIVE_INFINITY;
    if (tick <= segStart) return seconds;
    if (tick <= segEnd) {
      return seconds + tickDeltaToSeconds(tick - segStart, seg.bpm, safePpq);
    }
    seconds += tickDeltaToSeconds(segEnd - segStart, seg.bpm, safePpq);
  }
  return seconds;
};

export const secondsToTick = (
  seconds: number,
  tempos: { bpm: number; ticks: number }[],
  ppq: number
): number => {
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  const safePpq = ppq > 0 ? ppq : 480;
  const segments = normalizeTempos(tempos);
  let remaining = seconds;
  let currentTick = 0;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!;
    const segStart = seg.ticks;
    const segEnd =
      i + 1 < segments.length ? segments[i + 1]!.ticks : Number.POSITIVE_INFINITY;
    const segTicks = segEnd === Number.POSITIVE_INFINITY
      ? Number.POSITIVE_INFINITY
      : segEnd - segStart;
    if (segTicks === Number.POSITIVE_INFINITY) {
      return currentTick + secondsToTickDelta(remaining, seg.bpm, safePpq);
    }
    const segDuration = tickDeltaToSeconds(segTicks, seg.bpm, safePpq);
    if (remaining <= segDuration) {
      return segStart + (remaining / segDuration) * segTicks;
    }
    remaining -= segDuration;
    currentTick = segEnd;
  }
  return currentTick;
};

export const formatClock = (totalSeconds: number): string => {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "00:00.000";
  const ms = Math.floor((totalSeconds % 1) * 1000);
  const total = Math.floor(totalSeconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  const base = h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  return `${base}.${ms.toString().padStart(3, "0")}`;
};